/**
 * O que os cartões de tarefa do chat FAZEM quando alguém clica — spec-010,
 * [T016] / US-2, decisão D8.
 *
 * "Abrir o registro" não é uma resposta ao modelo: é abrir um modal ou empurrar
 * uma rota, e as duas coisas se resolvem no browser, agora. Mandá-las pelo
 * `respond` obrigaria quem está acima do `chat-message` a conhecer o modal de
 * cada módulo — exatamente o acoplamento que o registry existe para desfazer.
 *
 * Por isso estas funções são chamadas pelos `efeitos` do embrulho
 * (`ui-registry/cartao-do-chat.js`), e não pelo `.vue` do cartão: o cartão
 * emite, o embrulho executa, e o cartão continua montável num teste sem router
 * nem `GlobalBus`.
 */
import { MODAL } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import { temContextoDeNavegacao } from '@modules/aawz-intelligence-v2/ui-registry/tools-de-front/catalogo'
import { abrirRotaDoCartao } from '@modules/aawz-intelligence-v2/ui-registry/acoes-compartilhadas'

/**
 * A rota do board com sufixo de deep link (`modules/tarefas/routes.js:98`).
 *
 * É a ÚNICA rota real da Gestão de Tarefas que aceita apontar para um registro:
 * `t/<8 chars da tarefa>` para uma tarefa e `l/<pasta>/s/<lista>` para uma
 * lista. O `shortlink` já vem pronto do backend — o front não o recorta.
 */
export const ROTA_DO_BOARD = 'task.board.list'

// A mensagem traz a SAÍDA junto: erro sem próxima ação deixa o consultor
// travado na conversa sem saber por onde chegar ao board.
const ERRO_AO_NAVEGAR =
  'Não foi possível abrir o gerenciador de tarefas. Tente pelo menu Tarefas.'

/**
 * Abre o modal de detalhe da tarefa.
 *
 * O payload é o mesmo que o board manda (`task-detail-modal.vue:156`): com
 * `task.id` o modal entra em modo edição e busca o detalhe sozinho. Sem id não
 * há o que abrir — e abrir o modal vazio criaria uma tarefa nova, que é o
 * oposto do que o clique pediu.
 */
export function abrirTarefa(id) {
  if (!id) return false
  GlobalBus.$emit(MODAL.TASK_DETAIL, { task: { id: id } })
  return true
}

/**
 * [bug corrigido, 30/08/2026] `{ name: ROTA_DO_BOARD, params: { shortlink } }`
 * ia direto pro `router.resolve` — e o vue-router 3 faz `encodeURIComponent`
 * em CADA valor de `params` antes de casar contra o regex da rota. O padrão
 * desta rota É multi-segmento (`l/<pasta>/s/<lista>`, barras DE VERDADE), e o
 * `%2F` que sai do encode nunca bate com `l\/.*|t\/.*` — o resolve falha
 * silenciosamente e cai pra "/", que é o que abria (numa aba nova, por causa
 * do `ABRIR_TELA_EM_NOVA_ABA`) sempre que a lista tinha shortlink.
 *
 * A saída: resolver a rota BASE (sem shortlink, só o `name` — aí não tem
 * param nenhum pro vue-router encodar) pra pegar o path de verdade, correto
 * mesmo com prefixo aninhado, e concatenar o shortlink CRU nele. `{ path }`
 * não passa pelo preenchimento de `params` — vai pro matcher como string.
 */
function rotaDoBoardComShortlink(router, shortlink) {
  const base = router.resolve({ name: ROTA_DO_BOARD })
  const path = (base && base.href) || ''
  return { path: path.replace(/\/$/, '') + '/' + shortlink }
}

/**
 * Empurra o board, com o deep link quando ele existe.
 *
 * `empurrarRota` é o do catálogo das tools de front, e não um `push` cru: ele
 * engole o `NavigationDuplicated` do vue-router 3. Sem isso, clicar em "abrir"
 * com o board JÁ aberto viraria uma promise rejeitada solta no meio do turno.
 *
 * ★ Ele engole SÓ o push redundante e RELANÇA o resto — guarda de rota,
 * `NavigationCancelled`, `NavigationRedirected`. Por isso o `.catch`: sem ele a
 * falha vira promise rejeitada no console e um clique que não faz nada, sem
 * mensagem, que é o sintoma que o par do CRM já evitava (`crm/ia/acoes.js`).
 *
 * @param {Object} vm         A instância do embrulho (de onde saem `$router` e
 *                            `$showSnack`).
 * @param {string} shortlink  Sufixo do deep link, ou vazio para o board todo.
 * @returns {Promise<boolean>} `false` quando a superfície não tem router ou
 *                            quando a navegação falhou.
 */
export function abrirNoBoard(vm, shortlink) {
  const ctx = { router: vm && vm.$router }
  if (!temContextoDeNavegacao(ctx)) return Promise.resolve(false)

  const rota = shortlink
    ? rotaDoBoardComShortlink(ctx.router, shortlink)
    : { name: ROTA_DO_BOARD, params: {} }

  // A receita (`empurrarRota` → aviso de aba barrada → snack com a saída) é a
  // de `ui-registry/acoes-compartilhadas`; ela resolve `null` na falha (já
  // avisada). Aqui isso vira o boolean "abriu?" que os embrulhos deste módulo
  // devolvem — aba barrada conta como "não abriu": o board não está na tela.
  return abrirRotaDoCartao(vm, rota, { mensagemDeErro: ERRO_AO_NAVEGAR }).then(
    (resultado) => Boolean(resultado) && !resultado.bloqueado
  )
}
